import { 
  updateRiskPriority,
  fetchRiskEvaluations
} from '../../redux/actions/riskActions';

export class RiskEvaluationService {
  constructor(dispatch, modelId, assessmentUuid, contextData = {}, liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.contextData = contextData;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeEvaluation(areaName, riskFormData, riskModel, progressCallback) {
    try {
      if (progressCallback) progressCallback('evaluation');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Get fresh Redux state instead of stale riskModel parameter
      const getState = () => this.dispatch((dispatch, getState) => getState());
      const currentState = getState();
      const freshRiskModel = currentState.risks.models[this.modelId] || {};
      const areaRisks = freshRiskModel.riskFormData[areaName]?.sectionRisks || {};
      
      // PROOF LOG: Show stale vs fresh state comparison
      console.log(`[RiskEvaluationService] 🔍 PROOF LOG for area: ${areaName}`);
      console.log(`[RiskEvaluationService] STALE riskModel parameter:`, riskModel.riskFormData[areaName]?.sectionRisks || {});
      console.log(`[RiskEvaluationService] FRESH Redux state:`, areaRisks);
      console.log(`[RiskEvaluationService] Available risk sources (FRESH):`, Object.keys(areaRisks));
      console.log(`[RiskEvaluationService] riskFormData parameter:`, riskFormData[areaName]);
      
      // Find risks that need evaluation
      const risksToEvaluate = [];
      
      console.log(`[RiskEvaluationService] 🔍 Searching for unevaluated risks in area: ${areaName}`);
      console.log(`[RiskEvaluationService] Available risk sources:`, Object.keys(areaRisks));
      
      Object.entries(areaRisks).forEach(([sourceName, riskArray]) => {
        console.log(`[RiskEvaluationService] Checking source "${sourceName}": ${Array.isArray(riskArray) ? riskArray.length : 'not array'} risks`);
        if (Array.isArray(riskArray)) {
          riskArray.forEach((risk, idx) => {
            console.log(`[RiskEvaluationService] Risk ${idx}: "${risk.riskName}" - status: ${risk.status}, priority: ${risk.priority}`);
            if (risk.status === 'Saved' && !risk.priority) {
              console.log(`[RiskEvaluationService] ✅ FOUND unevaluated risk: "${risk.riskName}"`);
              risksToEvaluate.push({
                ...risk,
                areaName,
                sourceName
              });
            }
          });
        }
      });

      // Use FastTrack AI evaluation with context-aware analysis
      if (risksToEvaluate.length > 0) {
        console.log(`[RiskEvaluationService] Evaluating ${risksToEvaluate.length} risks using FastTrack approach`);
        
        let evaluatedCount = 0;
        for (const risk of risksToEvaluate) {
          try {
            // Get causes data for this risk's source
            const riskAreaData = riskFormData[areaName];
            const sourceCauses = this.extractCausesForRisk(risk, riskAreaData);
            
            // Call FastTrack evaluation with full context
            const evalResponse = await fetch('/.netlify/functions/fastTrackRiskEvaluation', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                risk,
                causes: sourceCauses,
                contextData: this.contextData,
                modelUuid: this.modelId,
                assessmentUuid: this.assessmentUuid,
                riskArea: areaName,
                sourceName: risk.sourceName
              })
            });
            
            if (evalResponse.ok) {
              const evalData = await evalResponse.json();
              if (evalData.fastTrackEvaluation) {
                const evaluation = evalData.fastTrackEvaluation;
                const priority = evaluation.priority?.toLowerCase() || 'moderate';
                const rationale = evaluation.rationale || '';
                
                console.log(`[RiskEvaluationService] FastTrack evaluated ${risk.riskName}: ${priority}`);
                
                // Save the FastTrack evaluation using the same pattern as co-pilot
                await this.saveFastTrackEvaluation(risk, evaluation, priority, rationale, areaName);
                
                evaluatedCount++;
                
                // LIVE UPDATE: Stream each risk evaluation as it completes
                if (this.liveUpdateCallback) {
                  this.liveUpdateCallback('riskEvaluation', {
                    riskName: risk.riskName,
                    priority: priority,
                    rationale: rationale,
                    confidence: evaluation.confidence,
                    themes: evaluation.fastTrackData?.selectedThemes?.length || 0,
                    evaluatedCount,
                    totalRisks: risksToEvaluate.length
                  });
                }
              }
            } else {
              console.error(`[RiskEvaluationService] FastTrack evaluation failed for ${risk.riskName}:`, await evalResponse.text());
            }
          } catch (evalError) {
            console.error(`[RiskEvaluationService] Risk evaluation failed for ${risk.riskName}:`, evalError);
          }
        }
      }

      return {
        evaluatedRisks: risksToEvaluate.length,
        areaName
      };

    } catch (error) {
      console.error('[RiskEvaluationService] Evaluation failed:', error);
      throw error;
    }
  }

  // Extract causes data for a specific risk's source
  extractCausesForRisk(risk, riskAreaData) {
    try {
      // First try: from riskSources array
      const fromRiskSources = riskAreaData?.riskSources?.find(src => src.name === risk.sourceName)?.causes;
      if (fromRiskSources && Object.keys(fromRiskSources).length > 0) {
        return Object.entries(fromRiskSources).map(([name, data]) => ({
          name,
          description: data.description || data.rationale || '',
          ...data
        }));
      }
      
      // Second try: from causesData if it exists
      const fromCausesData = riskAreaData?.causesData?.[risk.sourceName];
      if (fromCausesData && Object.keys(fromCausesData).length > 0) {
        return Object.entries(fromCausesData).map(([name, data]) => ({
          name,
          description: data.description || data.rationale || '',
          ...data
        }));
      }
      
      console.log(`[RiskEvaluationService] No causes found for risk source: ${risk.sourceName}`);
      return [];
    } catch (error) {
      console.error('[RiskEvaluationService] Error extracting causes:', error);
      return [];
    }
  }

  // Save FastTrack evaluation results using EXACTLY the same pattern as co-pilot
  async saveFastTrackEvaluation(risk, evaluation, priority, rationale, areaName) {
    try {
      // STEP 1: Save each selected theme evaluation separately (same as co-pilot FastTrack)
      if (evaluation.fastTrackData?.selectedThemes?.length > 0) {
        const savePromises = evaluation.fastTrackData.selectedThemes.map(theme => 
          fetch('/.netlify/functions/saveRiskEvaluation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              modelUuid: this.modelId,
              assessmentUuid: this.assessmentUuid,
              riskName: risk.riskName,
              riskId: risk.riskId,
              themeId: theme.themeId,
              riskArea: areaName,
              priority: priority,
              rationale: rationale,
              data: theme.evaluation?.impact || theme.highLevelAssessment || {},
            }),
          })
        );

        // Wait for all theme saves to complete
        const responses = await Promise.all(savePromises);
        const failedSaves = responses.filter(response => !response.ok);
        
        if (failedSaves.length > 0) {
          console.error(`[RiskEvaluationService] Failed to save ${failedSaves.length} theme evaluations for ${risk.riskName}`);
        } else {
          console.log(`[RiskEvaluationService] Successfully saved ${evaluation.fastTrackData.selectedThemes.length} theme evaluations for ${risk.riskName}`);
        }

        // STEP 2: Update Redux state with priority and rationale (EXACT same as co-pilot)
        await this.dispatch(
          updateRiskPriority(
            this.modelId,
            risk.riskId,
            areaName, // riskArea (co-pilot uses riskArea as 3rd param)
            risk.sourceName, // source.name (co-pilot uses source.name as 4th param) 
            priority,
            this.assessmentUuid,
            rationale
          )
        );

        // STEP 3: Refresh evaluation data from database to get complete evaluationData (EXACT same as co-pilot)
        await this.dispatch(fetchRiskEvaluations(this.modelId, this.assessmentUuid, areaName, risk.sourceName));
      }
    } catch (error) {
      console.error(`[RiskEvaluationService] Error saving FastTrack evaluation for ${risk.riskName}:`, error);
    }
  }
}